import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BookOpen,
  Copy,
  ExternalLink,
  KeyRound,
  Plus,
  Trash2,
} from "lucide-react";
import {
  createApiKey,
  deleteApiKey,
  listApiKeys,
  PublicApiKey,
} from "../../api/apiKeys";
import styles from "./styles.stylesheet.module.scss";

const formatDate = (value: string | null) => {
  if (!value) {
    return "Never";
  }
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const sortedKeys = (keys: PublicApiKey[]) => {
  return [...keys].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
};

export const APIKeys = () => {
  const [keys, setKeys] = useState<PublicApiKey[]>([]);
  const [name, setName] = useState("");
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [createdApiKeyId, setCreatedApiKeyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sortedApiKeys = useMemo(() => sortedKeys(keys), [keys]);
  const activeKeysLabel =
    sortedApiKeys.length === 1
      ? "1 active key"
      : `${sortedApiKeys.length} active keys`;

  const loadApiKeys = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    const response = await listApiKeys();
    setKeys(response);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMessage("Name is required.");
      return;
    }

    setIsCreating(true);
    setStatusMessage(null);
    setErrorMessage(null);
    const response = await createApiKey(trimmedName);
    setKeys((items) => [response.key, ...items]);
    setCreatedApiKey(response.api_key);
    setCreatedApiKeyId(response.key.id);
    setName("");
    setStatusMessage("API key created.");
    setIsCreating(false);
  };

  const handleCopy = async () => {
    if (!createdApiKey) {
      return;
    }
    await navigator.clipboard.writeText(createdApiKey);
    setStatusMessage("Copied API key.");
  };

  const handleDelete = async (id: string) => {
    setDeletingKeyId(id);
    setStatusMessage(null);
    setErrorMessage(null);
    await deleteApiKey(id);
    setKeys((items) => items.filter((item) => item.id !== id));
    if (createdApiKeyId === id) {
      setCreatedApiKey(null);
      setCreatedApiKeyId(null);
    }
    setDeletingKeyId(null);
  };

  return (
    <main className={styles.apiKeysPage}>
      <header className={styles.apiKeysHeader}>
        <div>
          <h1>Developer</h1>
          <p>Create API keys and build with your Replay memories.</p>
        </div>
        <a
          className={styles.docsButton}
          href="https://docs.replayglasses.com"
          target="_blank"
          rel="noreferrer"
        >
          <BookOpen size={17} aria-hidden="true" />
          View docs
          <ExternalLink size={14} aria-hidden="true" />
        </a>
      </header>

      <section className={styles.apiKeysPanel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Active keys</h2>
            <p>{activeKeysLabel}</p>
          </div>
        </div>

        {isLoading ? (
          <div className={styles.emptyState}>Loading API keys...</div>
        ) : sortedApiKeys.length === 0 ? (
          <div className={styles.emptyState}>No API keys created yet.</div>
        ) : (
          <div className={styles.keysList}>
            {sortedApiKeys.map((apiKey) => (
              <article className={styles.keyRow} key={apiKey.id}>
                <div className={styles.keyIdentity}>
                  <KeyRound size={18} aria-hidden="true" />
                  <div>
                    <h3>{apiKey.name}</h3>
                    <span>{apiKey.id}</span>
                  </div>
                </div>
                <dl className={styles.keyMeta}>
                  <div>
                    <dt>Created</dt>
                    <dd>{formatDate(apiKey.created_at)}</dd>
                  </div>
                  <div>
                    <dt>Last used</dt>
                    <dd>{formatDate(apiKey.last_used_at)}</dd>
                  </div>
                </dl>
                <button
                  className={styles.deleteButton}
                  type="button"
                  onClick={() => handleDelete(apiKey.id)}
                  disabled={deletingKeyId === apiKey.id}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  {deletingKeyId === apiKey.id ? "Deleting..." : "Delete"}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={styles.apiKeysPanel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Create key</h2>
            <p>Keys are shown once after creation.</p>
          </div>
        </div>

        <form className={styles.createForm} onSubmit={handleCreate}>
          <label className={styles.field}>
            <span>Name</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Zapier workflow..."
              maxLength={80}
              disabled={isCreating}
            />
          </label>
          <button
            className={styles.primaryButton}
            type="submit"
            disabled={isCreating || !name.trim()}
          >
            <Plus size={17} aria-hidden="true" />
            {isCreating ? "Creating..." : "Create key"}
          </button>
        </form>

        {createdApiKey && (
          <div className={styles.createdKeyPanel}>
            <div className={styles.createdKeyHeader}>
              <KeyRound size={18} aria-hidden="true" />
              <strong>New API key</strong>
            </div>
            <div className={styles.createdKeyValue}>
              <code>{createdApiKey}</code>
              <button type="button" onClick={handleCopy}>
                <Copy size={16} aria-hidden="true" />
                Copy
              </button>
            </div>
          </div>
        )}

        {statusMessage && (
          <p className={styles.status}>
            <span>{statusMessage}</span>
            {createdApiKey && statusMessage === "API key created." && (
              <span className={styles.statusWarning}>
                Copy it now. Replay cannot show the full key again.
              </span>
            )}
          </p>
        )}
        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
      </section>
    </main>
  );
};
